import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/lib/brand";
import { billingLine, envelopesLine, inr } from "@/lib/plans";
import { ButtonPayOverlay } from "@/components/site/ButtonPay";

const FIELDS = [
  ["full_name", "Full name", "sm:col-span-2"],
  ["email", "Email", ""],
  ["phone", "Phone (10 digits)", ""],
  ["address_line1", "Address line 1", "sm:col-span-2"],
  ["address_line2", "Address line 2 (optional)", "sm:col-span-2"],
  ["city", "City", ""],
  ["state", "State", ""],
  ["pincode", "PIN code", ""],
];
const EMPTY = Object.fromEntries([...FIELDS.map(([k]) => [k, ""]), ["gift_note", ""]]);

const validate = (f) => {
  if (!/^[6-9]\d{9}$/.test(f.phone)) return "Please enter a valid 10-digit Indian mobile number.";
  if (!/^[1-9]\d{5}$/.test(f.pincode)) return "Please enter a valid 6-digit PIN code.";
  return null;
};

const errText = (ex) => {
  const d = ex.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(" ");
  return typeof d === "string" ? d : ex.message || "Something went wrong.";
};

const Summary = ({ plan }) => (
  <div className="rounded-2xl border border-amber-200 bg-[#FFFBEB] p-4 text-sm" data-testid="checkout-order-summary">
    <div className="flex justify-between font-medium text-stone-900"><span data-testid="checkout-summary-plan">{plan.name}</span><span data-testid="checkout-summary-price">{inr(plan.price)}</span></div>
    <p className="text-stone-600 mt-1">{billingLine(plan)} · {envelopesLine(plan)}{plan.recurring ? " · auto-renews" : ""} · free shipping in India</p>
  </div>
);

export const SubscribeDialog = ({ open, onOpenChange, plan }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [button, setButton] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const err = validate(form);
    if (err) return toast.error(err);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/subscriptions`, { plan_id: plan.id, customer: form });
      setButton({ id: data.payment_button_id, kind: data.button_kind });
    } catch (ex) {
      toast.error(errText(ex));
    }
    setBusy(false);
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) { setBusy(false); if (button) { setButton(null); setForm(EMPTY); } }
  };

  if (button) {
    return <ButtonPayOverlay buttonId={button.id} kind={button.kind} plan={plan} onClose={() => close(false)} />;
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto bg-[#FFFDF9]" data-testid="checkout-dialog">
        {(
          <>
            <DialogHeader>
              <DialogTitle className="font-serif-display text-3xl font-medium" data-testid="checkout-title">{plan.recurring ? `Subscribe · ${plan.durationLabel}` : "One-time envelope"}</DialogTitle>
              <DialogDescription>Where should we send your envelopes? We ship within India only.</DialogDescription>
            </DialogHeader>
            <Summary plan={plan} />
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 mt-2" data-testid="checkout-form">
              {FIELDS.map(([k, label, span]) => (
                <div key={k} className={`space-y-1.5 ${span}`}>
                  <Label htmlFor={k}>{label}</Label>
                  <Input id={k} type={k === "email" ? "email" : "text"} required={k !== "address_line2"} value={form[k]} onChange={set(k)} data-testid={`checkout-${k.replace(/_/g, "-")}-input`} />
                </div>
              ))}
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="gift_note">Note (optional)</Label>
                <Textarea id="gift_note" maxLength={300} value={form.gift_note} onChange={set("gift_note")} data-testid="checkout-gift-note-input" />
              </div>
              <button type="submit" disabled={busy} className="sm:col-span-2 mt-2 rounded-full bg-stone-900 text-white py-4 hover:bg-amber-600 transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2" data-testid="checkout-pay-button">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save & continue to payment
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
